import { LoaderCircle, RefreshCw } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { ACTUAL_COMPARISON_ALL_KEY } from "../../hooks/useStrategyVsActualComparison";
import {
  ADMIN_CUSTOMER_ACCOUNT_MAX_AGE_MS,
  ADMIN_CUSTOMER_ACCOUNT_QUERY_OPTIONS,
  useAdminCustomerViewAccountSummary,
} from "../../hooks/useAdminCustomerViewAccountData";
import investorStrategyService, {
  getSignedWeight,
  type InvestorStrategyRecord,
} from "../../services/investorStrategyService";
import { productSearchService } from "../../services/productSearchService";
import {
  formatCurrencyWithCommas,
  formatPercentage,
  formatSignedPercentage,
} from "../../utils/numberUtils";
import { computeStrategyTotals } from "../../utils/strategyTotals";
import { getReconciledCurrentWeight } from "../../utils/strategyUtils";
import type { AlphaDashboardPreviewPageProps } from "./alphaPageSubject";
import { getTargetConfigValue } from "./alphaPageSubject";
import AlphaPreferencesCard from "./AlphaPreferencesCard";
import AlphaWhyManagedTeaser from "./AlphaWhyManagedTeaser";
import AlphaPerformanceChartContent from "./AlphaPerformanceChartContent";
import { useAdminCustomerViewStrategyPerformance } from "./useAdminCustomerViewStrategyPerformance";

type AllocationNameMap = Record<string, string>;
const ALPHA_ALLOCATION_BASIS_PCT = 100;

function allocationKey(entry: InvestorStrategyRecord): string {
  return `${entry.entityType}-${entry.productId}`;
}

function getCurrentWeight(entry: InvestorStrategyRecord): number {
  return getReconciledCurrentWeight(entry) ?? getSignedWeight(entry);
}

function allocationLabel(
  entry: InvestorStrategyRecord,
  allocationNames: AllocationNameMap,
  includeManagedProductId = false
): string {
  if (entry.entityType === "MANAGED_STRATEGY") {
    return includeManagedProductId
      ? `Plutus Managed #${entry.productId}`
      : "Plutus Managed";
  }
  const resolvedName = allocationNames[allocationKey(entry)];
  if (resolvedName) return resolvedName;
  const productType = entry.entityType === "PORTFOLIO" ? "Portfolio" : "Basket";
  return `${productType} ${entry.productId}`;
}

export default function AdminTargetDashboardAdapter(
  props: Extract<AlphaDashboardPreviewPageProps, { mode: "admin-target" }>
): React.ReactElement {
  const { subject, questionnaire, accountState, onRetryQuestionnaire, onRetryAccountPreflight } = props;
  const [strategies, setStrategies] = useState<InvestorStrategyRecord[]>([]);
  const [allocationNames, setAllocationNames] = useState<AllocationNameMap>({});
  const [nameLookupFailed, setNameLookupFailed] = useState(false);
  const [strategyStatus, setStrategyStatus] = useState<"loading" | "loaded" | "failed">("loading");
  const [strategyAttempt, setStrategyAttempt] = useState(0);

  const launchDate = getTargetConfigValue(subject.userConfig, "launch_date");
  const accountStateIsCurrent = accountState.ownerUserId === subject.userId
    && accountState.generation === subject.generation;
  const linked = accountStateIsCurrent && accountState.status === "linked";
  const summaryQuery = useAdminCustomerViewAccountSummary({
    targetUserId: subject.userId,
    enabled: linked,
    maxAgeMs: ADMIN_CUSTOMER_ACCOUNT_MAX_AGE_MS,
    queryOptions: ADMIN_CUSTOMER_ACCOUNT_QUERY_OPTIONS,
  });
  const performance = useAdminCustomerViewStrategyPerformance({
    targetUserId: subject.userId,
    enabled: true,
    includeActual: linked,
    launchDate,
    selectedKey: ACTUAL_COMPARISON_ALL_KEY,
  });

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    setStrategyStatus("loading");
    setAllocationNames({});
    setNameLookupFailed(false);
    void investorStrategyService.getStrategyRecordsForUser(subject.userId).then((records) => {
      if (!active) return;
      setStrategies(records);
      setStrategyStatus("loaded");
      const unique = new Map<string, { productType: "PORTFOLIO" | "BASKET"; productId: number }>();
      records.forEach((entry) => {
        if (entry.entityType === "MANAGED_STRATEGY") return;
        unique.set(allocationKey(entry), {
          productType: entry.entityType,
          productId: entry.productId,
        });
      });
      void Promise.all(Array.from(unique.entries()).map(async ([key, entry]) => {
        try {
          const response = await productSearchService.searchAdminProducts({
            page: 1,
            pageSize: 1,
            productType: entry.productType,
            productId: entry.productId,
          }, controller.signal);
          const product = response.items.find((item) => (
            item.productType === entry.productType && item.productId === entry.productId
          ));
          if (!product) {
            if (active) setNameLookupFailed(true);
            return;
          }
          if (!controller.signal.aborted && active) {
            setAllocationNames((current) => ({ ...current, [key]: product.title }));
          }
        } catch {
          if (active) setNameLookupFailed(true);
        }
      }));
    }).catch(() => {
      if (active) setStrategyStatus("failed");
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [subject.userId, strategyAttempt]);

  const allocations = strategies;
  const allocationTotals = useMemo(
    () => computeStrategyTotals(allocations, ALPHA_ALLOCATION_BASIS_PCT, null),
    [allocations]
  );
  const cashPct = Math.max(0, allocationTotals.remainingCurrentPct);
  const managedCount = strategies.filter((row) => row.entityType === "MANAGED_STRATEGY").length;
  const isFullySelfDirected = strategies.length > 0 && managedCount === 0;
  const summaryOwned = linked
    && summaryQuery.error === null
    && summaryQuery.loadedForUserId === subject.userId
    && summaryQuery.data !== undefined;
  const ownedSummary = summaryOwned ? summaryQuery.data : undefined;
  const selectedReturn = performance.chart.strategyTimeframePerformance["Since inception"] ?? null;

  let summaryBlock: React.ReactNode;
  if (!accountStateIsCurrent || accountState.status === "loading") {
    summaryBlock = (
      <div className="h-12 w-40 animate-pulse rounded bg-v7-mist" role="status">Loading account summary</div>
    );
  } else if (accountState.status === "none") {
    summaryBlock = <h2 className="text-base font-semibold text-v7-ink">No linked brokerage account</h2>;
  } else if (accountState.status === "failed") {
    summaryBlock = (
      <div>
        <h2 className="text-base font-semibold text-v7-ink">Account unavailable</h2>
        <p className="mt-1 text-sm text-v7-ink-soft">
          We could not verify this customer&apos;s linked brokerage account.
        </p>
        <button
          type="button"
          onClick={onRetryAccountPreflight}
          className="mt-3 rounded-v7-control bg-v7-ink px-3 py-2 text-sm font-medium text-v7-white"
        >
          Retry account check
        </button>
      </div>
    );
  } else if (summaryQuery.isLoading) {
    summaryBlock = (
      <div className="h-12 w-40 animate-pulse rounded bg-v7-mist" role="status">Loading account summary</div>
    );
  } else if (!ownedSummary) {
    summaryBlock = (
      <div>
        <h2 className="text-base font-semibold text-v7-ink">Account summary unavailable</h2>
        <p className="mt-1 text-sm text-v7-ink-soft">
          We could not load a verified account summary for this customer.
        </p>
        <button
          type="button"
          onClick={() => { void summaryQuery.refetch(); }}
          disabled={summaryQuery.isFetching}
          className="mt-3 rounded-v7-control bg-v7-ink px-3 py-2 text-sm font-medium text-v7-white"
        >
          Retry summary
        </button>
      </div>
    );
  } else {
    summaryBlock = (
      <span className="ph-mask font-v7-serif text-[38px] leading-none tracking-[-0.04em] text-v7-ink">
        {formatCurrencyWithCommas(ownedSummary.netLiquidationSecurity, 0)}
      </span>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1180px] px-4 pb-16 pt-7 md:px-10">
      <section className="rounded-v7-panel border border-v7-ink/15 bg-v7-white p-5">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-medium uppercase tracking-[0.14em] text-v7-bronze-dark">
              Portfolio value
            </p>
            <div className="mt-2">{summaryBlock}</div>
            {selectedReturn !== null && summaryOwned && (
              <span className={`mt-2 block font-mono text-sm ${selectedReturn >= 0 ? "text-v7-success" : "text-v7-rust"}`}>
                {formatSignedPercentage(selectedReturn, 1)}
              </span>
            )}
          </div>
        </div>
        <div data-testid="alpha-strategy-performance-chart" className="px-1 pb-2 pt-4">
          <AlphaPerformanceChartContent
            {...performance.chart}
            chartHeight={390}
            isDarkMode={false}
            onRetry={performance.retry}
            showUnavailablePeriods
          />
        </div>
        {performance.actualError && (
          <div role="alert" className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-v7-ink/15 pt-3">
            <p className="text-sm text-v7-rust">{performance.actualError}</p>
            <button
              type="button"
              onClick={performance.retryActual}
              disabled={performance.isActualFetching}
              className="min-h-10 rounded-v7-control border border-v7-rust/30 bg-v7-white px-3 text-xs font-medium text-v7-rust disabled:cursor-not-allowed disabled:opacity-50"
            >
              {performance.isActualFetching ? "Retrying actual performance" : "Retry actual performance"}
            </button>
          </div>
        )}
      </section>

      <section className="mt-7 grid gap-7 border-y border-v7-ink/15 py-7 md:grid-cols-2">
        <div>
          <h2 className="font-v7-serif text-2xl font-normal text-v7-ink">Allocations</h2>
          {nameLookupFailed && (
            <p role="status" className="mt-2 text-sm text-v7-bronze-dark">
              Some allocation names could not be loaded. Product IDs are shown instead.
            </p>
          )}
          <div className="mt-4 divide-y divide-v7-ink/15 border-y border-v7-ink/15">
            {strategyStatus === "loading" ? (
              <p role="status" className="py-4 text-sm text-v7-ink-soft">Loading allocations</p>
            ) : strategyStatus === "failed" ? (
              <div className="flex items-center justify-between gap-4 py-4">
                <p role="alert" className="text-sm text-red-700">Allocations are temporarily unavailable.</p>
                <button
                  type="button"
                  onClick={() => setStrategyAttempt((n) => n + 1)}
                  className="min-h-10 rounded-v7-control border border-v7-ink/30 bg-v7-white px-3 text-xs font-medium"
                >
                  Retry allocations
                </button>
              </div>
            ) : allocations.length === 0 ? (
              <p className="py-4 text-sm text-v7-ink-soft">No allocations.</p>
            ) : (
              allocations.map((entry) => {
                const currentWeight = getCurrentWeight(entry);
                return (
                  <div key={allocationKey(entry)} className="flex items-center justify-between py-4">
                    <p className="text-sm font-medium text-v7-ink">
                      {allocationLabel(entry, allocationNames, managedCount > 1)}
                    </p>
                    <span className="font-v7-serif text-base text-v7-ink">
                      {formatPercentage(currentWeight)}
                    </span>
                  </div>
                );
              })
            )}
            {strategyStatus === "loaded" && allocations.length > 0 && (
              <div className="flex items-center justify-between py-4">
                <p className="text-sm text-v7-ink-soft">Cash</p>
                <span className="font-v7-serif text-base text-v7-ink-soft">
                  {formatPercentage(cashPct)}
                </span>
              </div>
            )}
          </div>
        </div>
        {questionnaire.status === "loading" ? (
          <div className="h-40 animate-pulse rounded-v7-panel bg-v7-mist" role="status" />
        ) : (
          <AlphaPreferencesCard
            progressResponses={questionnaire.status === "loaded" ? questionnaire.progressResponses : null}
            displayOnly
            requestFailed={questionnaire.status === "failed"}
            onRetry={onRetryQuestionnaire}
          />
        )}
      </section>

      {managedCount > 1 ? (
        <section className="mt-6 rounded-v7-panel border border-v7-ink/15 bg-v7-paper px-4 py-4">
          <p className="text-sm font-semibold text-v7-ink">Why Plutus Managed?</p>
          <p className="mt-1 text-[13px] text-v7-ink-soft">
            Details are unavailable because this account has multiple managed strategies.
          </p>
        </section>
      ) : (
        <AlphaWhyManagedTeaser mode="static" selfDirected={isFullySelfDirected} />
      )}
      {performance.chart.loading && (
        <LoaderCircle className="sr-only" />
      )}
      {performance.isActualFetching && <RefreshCw className="sr-only" />}
    </div>
  );
}
